import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banner, Button, Card, Field } from '../components/ui';
import { authApi, ApiClientError } from '../api/client';

/**
 * PUBLIC /reset-password?token=<raw> — set a new password from the emailed link.
 *
 * Reads the raw token from the URL query. On success the server has already
 * revoked existing sessions, so we just point the user back to /login.
 */
export function ResetPasswordPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('This reset link is invalid or has expired.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await authApi.confirmPasswordReset(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 400) {
        setError('This reset link is invalid or has expired.');
      } else {
        setError(err instanceof ApiClientError ? err.message : 'Could not reset password.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-box">
        <h1 className="auth-title">App Builder</h1>
        <Card title="Choose a new password">
          {done ? (
            <>
              <Banner tone="success">
                Your password has been reset. You can now sign in.
              </Banner>
              <p className="auth-alt">
                <Link to="/login">Go to sign in</Link>
              </p>
            </>
          ) : (
            <>
              <form onSubmit={onSubmit} className="form">
                {error && <Banner>{error}</Banner>}
                {!token && (
                  <Banner tone="info">
                    This reset link is missing its token. Request a new one from the
                    forgot-password page.
                  </Banner>
                )}
                <Field
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <Field
                  label="Confirm new password"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
                <Button type="submit" loading={busy} disabled={!token}>
                  Reset password
                </Button>
              </form>
              <p className="auth-alt">
                <Link to="/login">Back to sign in</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
